import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { AnalyticsService } from './analytics.service';

export type OriginationDashboardCategory = 'Unsubmitted' | 'PendingApproval' | 'Paying' | 'Historic';

export interface OriginationRedirectData {
  id: string;
  loanNumber: string;
  status: OriginationDashboardCategory;
  loanType: 'Purchase' | 'Refinance' | 'HELOC' | 'HomeEquity';
  redirectURL?: string;
}

@Injectable({ providedIn: 'root' })
export class OriginationRedirectService {
  private readonly router = inject(Router);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly translocoService = inject(TranslocoService);

  async handleApplicationAction(application: OriginationRedirectData): Promise<boolean> {
    const { status } = application;

    this.trackRedirectEvent(application);

    switch (status) {
      case 'Unsubmitted':
        return this.navigateToApplication(application);
      case 'PendingApproval':
      case 'Paying':
        return this.navigateToOriginationDashboard(application);
      case 'Historic':
        return this.navigateToAccountDetails(application);
      default:
        return false;
    }
  }

  shouldShowContinueButton(status: OriginationDashboardCategory): boolean {
    return status === 'Unsubmitted';
  }

  shouldShowViewPortalButton(status: OriginationDashboardCategory): boolean {
    return status === 'PendingApproval' || status === 'Paying' || status === 'Historic';
  }

  getActionButtonLabel(status: OriginationDashboardCategory): string {
    switch (status) {
      case 'Unsubmitted':
        return this.translocoService.translate('servicesButtonsContinueApplication');
      case 'PendingApproval':
        return this.translocoService.translate('servicesButtonsViewTasks');
      case 'Paying':
      case 'Historic':
        return this.translocoService.translate('servicesButtonsViewLoan');
      default:
        return this.translocoService.translate('accountSummaryViewDetails');
    }
  }

  private navigateToApplication(application: OriginationRedirectData): Promise<boolean> {
    const { loanType, id } = application;
    const route = loanType === 'Purchase' ? '/apply/purchase' : '/apply/refinance';
    return this.router.navigate([route], {
      queryParams: { loanId: id },
    });
  }

  private navigateToOriginationDashboard(application: OriginationRedirectData): Promise<boolean> {
    return this.router.navigate(['/origination', application.id]);
  }

  private navigateToAccountDetails(application: OriginationRedirectData): Promise<boolean> {
    return this.router.navigate(['/loans', application.loanNumber, 'details']);
  }

  private trackRedirectEvent(application: OriginationRedirectData): void {
    this.analyticsService.trackEvent('origination_application_action', {
      applicationId: application.id,
      status: application.status,
      loanType: application.loanType,
    });
  }
}
